// SPDX-License-Identifier: AGPL-3.0-or-later
import { useMemo } from "react";
import { categoryTree, flatten, SPECIAL_CATEGORIES } from "../lib/categories.js";
import { useApp } from "../lib/app.js";
import { useI18n } from "../lib/i18n.js";
import { useStore } from "../lib/store.js";

/**
 * The options for a category picker: one group per main category, holding
 * the category itself and everything under it. Sub-categories read as a
 * full path ("Housing › Utilities › Water") so a closed picker is never
 * ambiguous (Housing and Transport both have Repairs).
 */
export function CategoryOptions({ withIncome = true }: { withIncome?: boolean }) {
  const { store } = useApp();
  const state = useStore(store);
  const { category, categoryPath } = useI18n();
  const inUse = useMemo(() => new Set(state.transactions.flatMap((t) => (t.category ? [t.category] : []))), [state.transactions]);
  const tree = useMemo(() => categoryTree(state.profile, state.custom, inUse), [state.profile, state.custom, inUse]);
  return (
    <>
      {tree.map((main) => (
        <optgroup key={main.id} label={category(main.id)}>
          <option value={main.id}>{category(main.id)}</option>
          {flatten(main.children).map((c) => (
            <option key={c.id} value={c.id}>
              {categoryPath(c.id)}
            </option>
          ))}
        </optgroup>
      ))}
      {withIncome && (
        <optgroup label="—">
          {SPECIAL_CATEGORIES.map((id) => (
            <option key={id} value={id}>
              {category(id)}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
}
